import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import PropTypes from 'prop-types';
import { Box, Card, Typography, Button } from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { DateTime } from 'luxon';
import { useLocation, useNavigate } from 'react-router-dom';
import DateSelector from "../../../Inputs/DatePicker/DatePicker";
import Loader from "../../../Inputs/Loader/Loader";
import { http } from "../../../../clients/http/http.client";
import { HTTP_ENDPOINTS } from "../../../../clients/clients.constants";
import {formatDateTimeInTimeZone, formatDateTimeInTimeZoneInISO} from "../../../../helpers/useTimeZoneTime";
import { setPeakUsageSelectedDateAction } from "../../../../store/peakUsageSelectedDate";
import { setStoreDateRange } from "../../../../store/analyticsSelections";

const { EQUIPMENT_PEAK_USAGE } = HTTP_ENDPOINTS;

const PeakUsageCard = ({
                         title = 'Peak Usage',
                         subtitle = '',
                         siteId,
                         fixtures = [],
                         view,
                         navigatePath = '',
                         cardWidth = 245,
                       }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const selectedDate = useSelector((state) => state.peakUsageSelectedDate.selectedDate);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ peakInUse: 0, peakTime: null });
  const prevQsRef = useRef('');
  const timeZone = useSelector((state) => state.timeZone.timeZone);

  const buildQueryParams = ({ siteId, fixtures, locationSearch, date }) => {
    const params = new URLSearchParams();
    if (siteId) params.append('siteId', siteId);
    if (locationSearch.includes('arrc-inventory')) {
      params.delete('siteId');
      params.append('siteId', 1);
    }
    if (fixtures?.length) {
      params.append('equipmentTypeIds', fixtures.join(','));
    }
    if (date) {
      params.append('date', date);
    }
    return params.toString() ? `?${params.toString()}` : '';
  };

  const fixturesKey = useMemo(
    () => (fixtures?.length ? [...fixtures].sort().join(',') : ''),
    [fixtures]
  );

  useEffect(() => {
    if (selectedDate) {
      localStorage.setItem('peakSelectedDate', selectedDate);
    }
    if (!siteId || !selectedDate) return;

    const qs = buildQueryParams({
      siteId,
      fixtures,
      locationSearch: location.search,
      date: selectedDate,
    });

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await http.client.get(
          `${EQUIPMENT_PEAK_USAGE}${qs}`
        );
        if (!cancelled) {
          setData(data);
          prevQsRef.current = qs;
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load peak usage');
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [siteId, fixturesKey, location.search, selectedDate]);

  const handleDateChange = (newDate) => {
    dispatch(setPeakUsageSelectedDateAction(newDate));
  };

  // const handlePeakPeriodClick = (peakStart, peakEnd) => {
  //   console.log('peakStart', peakStart);
  //   console.log('peakEnd', peakEnd);
  //
  //   const startDate = formatDateTimeInTimeZoneInISO(peakStart, timeZone);
  //   const endDate = formatDateTimeInTimeZoneInISO(peakEnd, timeZone);
  //   console.log('startDate', startDate);
  //   console.log('endDate', endDate);
  //
  //   dispatch(setStoreDateRange({ start: startDate, end: endDate }));
  //
  //   // Navigate to the asset-tracking page with the were-in-use view
  //   navigate('/asset-tracking?view=were-in-use');
  // };

  const getPeakPeriodsTimeRange = (start, end) => {
    const formattedStart = DateTime.fromISO(start, { zone: 'utc' })
      .setZone(timeZone)
      .toFormat('hh:mm a');
    const formattedEnd = DateTime.fromISO(end, { zone: 'utc' })
      .setZone(timeZone)
      .toFormat('hh:mm a');
    return `${formattedStart} - ${formattedEnd}`;
  }

  useEffect(() => {
    console.log('data', data);
  }, [data]);

  return (
    <Card
      sx={{
        p: 2.5,
        width: cardWidth,
        height: '230px',
        boxShadow: 'none',
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1.25,
          gap: 1,
          cursor: 'pointer',
        }}
      >
        <EventBusyIcon sx={{ color: '#1B3665' }} />
        <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#1B3665' }}>
          {title}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 14, color: '#A0AEC0', mb: 1.5 }}>
        {subtitle}
      </Typography>
      <DateSelector value={selectedDate} onChange={handleDateChange} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          mt: 2,
          minHeight: 60,
        }}
      >
        {loading ? (
          <Loader sx={{ width: "100%", height: "100%" }} />
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            {!selectedDate ? (
              <Typography>No Date selected</Typography>
            ) : (
              <>
                <Typography
                  sx={{ fontSize: 32, fontWeight: 700, color: '#1B3665' }}
                >
                  {data?.peakInUse}
                </Typography>
                <Box sx={{ height: '90px', overflowY: 'auto' }}>
                  {data?.peakPeriods?.map((peakPeriod, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                        Time: {getPeakPeriodsTimeRange(peakPeriod?.peakStart, peakPeriod?.peakEnd)}
                      </Typography>
                      {/*<Button*/}
                      {/*  variant="outlined"*/}
                      {/*  size="small"*/}
                      {/*  onClick={() => handlePeakPeriodClick(peakPeriod?.peakStart, peakPeriod?.peakEnd)}*/}
                      {/*  sx={{ fontSize: 12, padding: '2px 8px' }}*/}
                      {/*>*/}
                      {/*  View*/}
                      {/*</Button>*/}
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Card>
  );
};

PeakUsageCard.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  siteId: PropTypes.string,
  fixtures: PropTypes.arrayOf(PropTypes.number),
  view: PropTypes.any,
  navigatePath: PropTypes.string,
  cardWidth: PropTypes.number,
};

export default PeakUsageCard;